import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { cn } from '@/lib/utils'

interface ArrowLinkProps {
    href: string
    label: string
    direction?: 'right' | 'left'
    className?: string
}

interface ArrowIconProps {
    direction: 'right' | 'left'
}

const ArrowIcon: FC<ArrowIconProps> = ({ direction }): ReactNode => {
    const transform = direction === 'left'
        ? 'translate(1 9) rotate(-180 12 4.904)'
        : 'translate(1 9)'

    return (
        <span
            aria-hidden='true'
            className='relative h-6 w-[14px] shrink-0 overflow-clip transition-[width] duration-500 ease-studio group-hover/arrow:w-6 group-focus-visible/arrow:w-6'
        >
            <svg
                viewBox='0 0 26 26'
                className={cn(
                    'absolute top-1/2 size-6 -translate-y-1/2 transition-[right] duration-500 ease-studio',
                    direction === 'left'
                        ? 'right-[-10px] group-hover/arrow:right-0 group-focus-visible/arrow:right-0'
                        : 'right-0'
                )}
            >
                <path
                    d='M24 4.904 L18.3 9.809 L18.3 5.7 L0 5.7 L0 4.109 L18.3 4.109 L18.3 0Z'
                    transform={transform}
                    fill='currentColor'
                />
            </svg>
        </span>
    )
}

interface ArrowCueProps {
    label: string
    className?: string
}

export const ArrowCue: FC<ArrowCueProps> = ({ label, className }): ReactNode => {
    return (
        <span
            className={cn(
                'relative inline-flex items-center gap-2 overflow-clip px-[2px] py-2 text-foreground transition-[gap] duration-500 ease-studio group-hover/arrow:gap-3 group-focus-visible/arrow:gap-3',
                className
            )}
        >
            <span className='t-link whitespace-pre text-left'>
                {label}
            </span>

            <ArrowIcon direction='right' />

            <span aria-hidden='true' className='absolute bottom-0 left-0 h-1 w-full'>
                <span className='absolute left-[-1px] top-[calc(50%-0.5px)] h-px w-0 bg-foreground transition-[width] duration-500 ease-studio group-hover/arrow:w-[104%] group-focus-visible/arrow:w-[104%]' />
            </span>
        </span>
    )
}

export const ArrowLink: FC<ArrowLinkProps> = ({
    href,
    label,
    direction = 'right',
    className
}): ReactNode => {
    return (
        <Link
            href={href}
            className={cn(
                'group/arrow relative inline-flex items-center gap-2 overflow-clip px-[2px] py-2 text-foreground transition-[gap] duration-500 ease-studio hover:gap-3 focus-visible:gap-3',
                className
            )}
        >
            {direction === 'left' && <ArrowIcon direction='left' />}

            <span className='t-link whitespace-pre text-left'>
                {label}
            </span>

            {direction === 'right' && <ArrowIcon direction='right' />}

            <span aria-hidden='true' className='absolute bottom-0 left-0 h-1 w-full'>
                <span className='absolute left-[-1px] top-[calc(50%-0.5px)] h-px w-0 bg-foreground transition-[width] duration-500 ease-studio group-hover/arrow:w-[104%] group-focus-visible/arrow:w-[104%]' />
            </span>
        </Link>
    )
}
